import {
  formatCancellationDateLocal,
  resolveFreeCancellationDisplayValue,
} from '@skygroup/shared/utils/formatCancellationDateLocal';

/** Whether a pricing row is currently refundable (unix-seconds cancel date). */
export function hotelRoomIsRefundable(freeCancelationDate: unknown): boolean {
  if (typeof freeCancelationDate !== 'number') return false;
  return freeCancelationDate * 1000 > Date.now();
}

function hotelRoomCancellationPenalties(priceRow: any): any {
  return priceRow?.additional_info?.payment_options?.payment_types?.[0]
    ?.cancellation_penalties;
}

function hotelRoomPaymentCurrencyCode(priceRow: any): string {
  const paymentType =
    priceRow?.additional_info?.payment_options?.payment_types?.[0];
  return paymentType?.show_currency_code ?? paymentType?.currency_code ?? '';
}

/** Guest composition line — mirrors B2C RenderRoomGuests + bot chat prose. */
export function formatHotelRoomGuests(priceRow: any): string {
  const parts: string[] = [];
  const pushGuests = (
    adult?: number,
    child?: number,
    infant?: number,
    prefix?: string,
  ) => {
    const guestParts: string[] = [];
    if (typeof adult === 'number' && adult > 0) {
      guestParts.push(`${adult} adult${adult === 1 ? '' : 's'}`);
    }
    if (typeof child === 'number' && child > 0) {
      guestParts.push(`${child} child${child === 1 ? '' : 'ren'}`);
    }
    if (typeof infant === 'number' && infant > 0) {
      guestParts.push(`${infant} infant${infant === 1 ? '' : 's'}`);
    }
    if (guestParts.length) {
      parts.push(prefix ? `${prefix}${guestParts.join(', ')}` : guestParts.join(', '));
    }
  };

  pushGuests(priceRow?.adult, priceRow?.child, priceRow?.infant);

  if (Array.isArray(priceRow?.multi_room_guests)) {
    priceRow.multi_room_guests.forEach(
      (guests: { adult?: number; child?: number; infant?: number }, index: number) => {
        pushGuests(
          guests?.adult,
          guests?.child,
          guests?.infant,
          `room ${index + 2}: `,
        );
      },
    );
  }

  return parts.join('; ');
}

/** Amenity keys for display — mirrors B2C RoomOption card footer. */
export function getHotelRoomAmenityKeys(priceRow: any): string[] {
  const meal = priceRow?.additional_info?.meal;
  const rawAmenities: string[] = Array.isArray(
    priceRow?.additional_info?.amenities_data,
  )
    ? priceRow.additional_info.amenities_data.filter(
        (amenity: string) => amenity !== meal,
      )
    : [];

  const keys = [...rawAmenities];

  if (typeof priceRow?.comment === 'string' && priceRow.comment.trim()) {
    for (const part of priceRow.comment.split(',')) {
      const trimmed = part.trim();
      if (
        trimmed &&
        !keys.some((key) => key.toLowerCase() === trimmed.toLowerCase())
      ) {
        keys.push(trimmed);
      }
    }
  }

  return keys;
}

/** Human-readable amenity labels when translations are unavailable. */
export function formatHotelRoomAmenityFallback(key: string): string {
  return key.replace(/[-_]+/g, ' ');
}

/**
 * Cancellation summary for cards + chat — mirrors B2C free-cancellation label
 * plus a compact penalty breakdown when tiers exist.
 */
export function formatHotelRoomCancellationSummary(priceRow: any): string {
  const penalties = hotelRoomCancellationPenalties(priceRow);
  const currencyCode = hotelRoomPaymentCurrencyCode(priceRow);
  const freeCancelDate = priceRow?.free_cancelation_date;
  const isRefundable = hotelRoomIsRefundable(freeCancelDate);

  if (!isRefundable) {
    return 'Non-refundable';
  }

  const displayValue = resolveFreeCancellationDisplayValue(
    freeCancelDate,
    penalties,
  );
  const dateLabel = displayValue
    ? formatCancellationDateLocal(displayValue)
    : '';

  let summary = dateLabel
    ? `Free cancellation until ${dateLabel}`
    : 'Free cancellation';

  const chargePolicies = (penalties?.policies ?? []).filter(
    (policy: any) => Number(policy?.amount_show) > 0,
  );

  if (chargePolicies.length > 0) {
    const feeParts = chargePolicies.slice(0, 3).map((policy: any) => {
      const amount = Number(policy.amount_show);
      const amountLabel = Number.isFinite(amount)
        ? amount.toFixed(2)
        : String(policy.amount_show ?? '');
      const feeAmount = currencyCode
        ? `${currencyCode} ${amountLabel}`
        : amountLabel;

      if (policy.start_at && policy.end_at) {
        return `${feeAmount} fee if cancelled ${formatCancellationDateLocal(policy.start_at)}–${formatCancellationDateLocal(policy.end_at)}`;
      }
      if (policy.end_at) {
        return `${feeAmount} fee if cancelled by ${formatCancellationDateLocal(policy.end_at)}`;
      }
      if (policy.start_at) {
        return `full price if cancelled from ${formatCancellationDateLocal(policy.start_at)}`;
      }
      return `${feeAmount} cancellation fee`;
    });
    summary += `; then ${feeParts.join('; ')}`;
  }

  return summary;
}

export interface HotelRoomOptionDetails {
  guests: string;
  cancellationSummary: string;
  amenityKeys: string[];
  isRefundable: boolean;
}

/** Extract card-ready details from a raw pricing row. */
export function getHotelRoomOptionDetails(priceRow: any): HotelRoomOptionDetails {
  return {
    guests: formatHotelRoomGuests(priceRow),
    cancellationSummary: formatHotelRoomCancellationSummary(priceRow),
    amenityKeys: getHotelRoomAmenityKeys(priceRow),
    isRefundable: hotelRoomIsRefundable(priceRow?.free_cancelation_date),
  };
}
